import { useState, useRef, useCallback, useEffect } from 'react';
import { ChatCommunicationService } from '../services/ChatCommunicationService';
import dayjs from 'dayjs';

/**
 * Hook for collaborative document editing with AI copilot
 * Uses the "reasoning" agent for document edits
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.agentId - ID of the AI agent to collaborate with
 * @param {string} options.documentContent - Current document content
 * @param {Function} options.onContentUpdate - Callback when content is updated
 * @param {string} options.conversationId - Optional conversation ID for context
 * @param {string} options.threadId - Optional thread ID for conversation context
 * @param {Function} options.onAddChatMessage - Callback to add messages to the chat
 */
export const useCollaborativeEdit = ({ 
  agentId, 
  documentContent, 
  onContentUpdate,
  conversationId: initialConversationId,
  threadId: initialThreadId,
  onAddChatMessage, // NEW: Callback to add messages to the chat
}) => {
  // State management
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [streamedContent, setStreamedContent] = useState('');
  const [conversationId, setConversationId] = useState(initialConversationId);
  const [threadId, setThreadId] = useState(initialThreadId);
  const [reasoningThreadId, setReasoningThreadId] = useState(null); // NEW: For reasoning agent thread
  const [error, setError] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  
  // NEW: DAG progress tracking
  const [dagProgress, setDagProgress] = useState([]);
  const [currentDagNode, setCurrentDagNode] = useState(null);
  
  // Refs for managing streaming state
  const subscriptionRef = useRef(null);
  const streamBufferRef = useRef('');
  const isStreamingRef = useRef(false);

  /**
   * Clean up subscription on unmount
   */
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  /**
   * Apply a change to the document with predictive state
   */
  const applyChange = useCallback((change) => {
    const newChange = {
      id: Date.now(),
      type: change.type || 'edit',
      content: change.content,
      oldContent: change.oldContent || documentContent, // Store original content
      timestamp: new Date().toISOString(),
      status: 'pending'
    };
    
    setPendingChanges(prev => [...prev, newChange]);
    
    // Apply the change optimistically
    if (onContentUpdate && change.content) {
      onContentUpdate(change.content);
    }
    
    return newChange.id;
  }, [onContentUpdate, documentContent]);

  /**
   * Confirm a pending change - keeps the new content
   */
  const confirmChange = useCallback((changeId) => {
    const change = pendingChanges.find(c => c.id === changeId);
    
    if (change) {
      console.log('✅ Confirming change:', changeId);
      // Content is already applied optimistically, so we just update status
      setPendingChanges(prev => 
        prev.map(c => 
          c.id === changeId 
            ? { ...c, status: 'confirmed' }
            : c
        )
      );
      
      // Add to history
      setEditHistory(prev => [...prev, { ...change, confirmedAt: new Date().toISOString() }]);
      
      // Remove from pending list after animation
      setTimeout(() => {
        setPendingChanges(prev => prev.filter(c => c.id !== changeId));
      }, 500);
    }
  }, [pendingChanges]);

  /**
   * Reject a pending change - reverts to old content
   */
  const rejectChange = useCallback((changeId) => {
    const change = pendingChanges.find(c => c.id === changeId);
    
    if (change && change.oldContent) {
      console.log('❌ Rejecting change, reverting to old content:', changeId);
      
      // Revert content immediately
      if (onContentUpdate) {
        onContentUpdate(change.oldContent);
      }
      
      setPendingChanges(prev => prev.filter(c => c.id !== changeId));
    } else {
      // Just remove if no old content stored
      setPendingChanges(prev => prev.filter(c => c.id !== changeId));
    }
  }, [pendingChanges, onContentUpdate]);

  /**
   * Send a message to the AI copilot (reasoning agent) to request edits
   * @param {string} instruction - The instruction/request for the AI
   * @param {Object} options - Optional configuration
   * @param {boolean} options.skipFullDocument - If true, don't include the full document (for selected text only)
   * @param {string} options.selectedText - The selected text (for logging)
   */
  const requestEdit = useCallback(async (instruction, options = {}) => {
    const { skipFullDocument = false, selectedText } = options;
    
    setIsThinking(true);
    setError(null);
    streamBufferRef.current = '';
    setStreamedContent('');
    
    // Clear previous DAG progress AND pending changes
    setDagProgress([]);
    setCurrentDagNode(null);
    setPendingChanges([]); // Clear any pending changes from previous requests

    // Build the instruction message
    // If skipFullDocument is true (selected text only), don't add the full document
    // This matches the chat behavior where only selected text is sent
    let fullMessage = instruction;
    
    if (!skipFullDocument && documentContent) {
      // Only add full document if not working with selected text only
      console.log('📄 Document content being sent:', {
        hasContent: !!documentContent,
        contentLength: documentContent?.length,
        contentPreview: documentContent?.substring(0, 200) + '...'
      });
      fullMessage = `${instruction}\n\nDocument to edit:\n${documentContent}`;
    } else if (skipFullDocument) {
      console.log('📝 Selected text only mode - not including full document');
      console.log('📝 Selected text:', selectedText?.substring(0, 100));
    }

    console.log('🚀 Sending edit request to reasoning agent');
    console.log('📤 Full message length:', fullMessage.length);
    console.log('📤 Instruction:', instruction);
    console.log('📤 Full message (first 500 chars):', fullMessage.substring(0, 500));
    console.log('📤 Mode:', skipFullDocument ? 'Selected text only' : 'Full document');
    console.log('📤 Selected text in message:', selectedText ? fullMessage.includes(selectedText) : 'N/A');
    
    // Verify selected text is included when in selected text mode
    if (skipFullDocument && selectedText && !fullMessage.includes(selectedText)) {
      console.warn('⚠️ WARNING: Selected text not found in fullMessage!');
      console.warn('⚠️ Selected text:', selectedText.substring(0, 100));
      console.warn('⚠️ Full message:', fullMessage.substring(0, 200));
    }

    try {
      // Use the dedicated reasoning agent method
      const chatService = new ChatCommunicationService('http');
      
      const params = {
        message_text: fullMessage,
        conversation_uuid: reasoningThreadId, // Use dedicated thread for reasoning
        sender_uuid: 'document-canvas-user',
        onProgress: (progressUpdate) => {
          console.log('📊 DAG Progress:', progressUpdate);
          
          // Update current node (only for running status)
          if (progressUpdate.status === 'running') {
            setCurrentDagNode(progressUpdate);
          } else if (progressUpdate.status === 'completed') {
            // Clear current node when completed
            setCurrentDagNode(null);
          }
          
          // Update progress list - merge by nodeName
          setDagProgress(prev => {
            const existingIndex = prev.findIndex(item => item.nodeName === progressUpdate.nodeName);
            
            if (existingIndex >= 0) {
              // Update existing node
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                ...progressUpdate,
              };
              return updated;
            } else {
              // Add new node
              return [...prev, progressUpdate];
            }
          });
        },
      };

      console.log('📤 Reasoning agent request params:', params);

      // Use the new dedicated method for reasoning agent
      const response = await chatService.sendReasoningAgentMessage(params);
      
      console.log('✅ Reasoning agent response:', response);

      setIsThinking(false);
      setIsStreaming(false);

      if (response.success) {
        // Store the thread ID for future messages
        if (response.data.conversationUuid) {
          setReasoningThreadId(response.data.conversationUuid);
        }

        // ALWAYS prioritize output.reasoning.result (reasoningResult field)
        // This is the definitive final answer from the reasoning agent
        const reasoningResult = response.data.reasoningResult;
        const messageText = response.data.messageText;
        
        console.log('📥 Canvas - Response received:', {
          hasReasoningResult: !!reasoningResult,
          reasoningResultLength: reasoningResult?.length || 0,
          reasoningResultPreview: reasoningResult?.substring(0, 100),
          hasMessageText: !!messageText,
          messageTextLength: messageText?.length || 0,
          messageTextPreview: messageText?.substring(0, 100),
        });
        
        // Use reasoningResult if available, otherwise fall back to messageText
        const finalContent = reasoningResult || messageText || '';
        
        const source = reasoningResult ? 'output.reasoning.result' : (messageText ? 'messageText (fallback)' : 'empty');
        console.log('✅ Canvas - Using result from:', source);
        console.log('✅ Canvas - Final content (length: ' + finalContent.length + '):', finalContent.substring(0, 200));
        
        // Log accumulated state for debugging if available
        if (response.data.accumulatedState) {
          console.log('📊 Canvas - Accumulated state from response:', {
            hasOutput: !!response.data.accumulatedState.output,
            hasReasoning: !!response.data.accumulatedState.output?.reasoning,
            reasoningKeys: response.data.accumulatedState.output?.reasoning ? Object.keys(response.data.accumulatedState.output.reasoning) : [],
            reasoningResultExists: !!response.data.accumulatedState.output?.reasoning?.result,
            reasoningResultLength: response.data.accumulatedState.output?.reasoning?.result?.length || 0,
          });
          
          // Verify output.reasoning.result exists
          if (response.data.accumulatedState.output?.reasoning?.result) {
            console.log('✅ Canvas - Confirmed output.reasoning.result exists in accumulated state');
            console.log('✅ Canvas - Accumulated reasoning.result preview:', response.data.accumulatedState.output.reasoning.result.substring(0, 200));
          } else {
            console.warn('⚠️ Canvas - output.reasoning.result not found in accumulated state');
            console.warn('⚠️ Canvas - Available paths:', JSON.stringify(response.data.accumulatedState, null, 2).substring(0, 500));
          }
        }

        // Check if content is empty or just "{}"
        const isEmptyContent = !finalContent || 
                               finalContent.trim().length === 0 || 
                               finalContent === '{}' || 
                               finalContent === '[]';

        if (isEmptyContent) {
          console.warn('⚠️ Reasoning agent returned empty content:', finalContent);
          console.warn('⚠️ Full response data:', response.data);
          // Still add a message to chat to show the agent responded
          if (onAddChatMessage) {
            onAddChatMessage({
              message: "I received your request but couldn't generate content. Please try rephrasing your instruction.",
              type: 'received',
              time: dayjs().format("HH:mm"),
              agentType: 'reasoning',
              metadata: {
                isReasoningAgent: true,
                instruction: instruction,
                isEmpty: true,
              }
            });
          }
          return; // Don't apply empty content to document
        }

        // Add the response to the chat with reasoning agent styling
        if (onAddChatMessage) {
          onAddChatMessage({
            message: finalContent,
            type: 'received',
            time: dayjs().format("HH:mm"),
            agentType: 'reasoning', // This identifies it as a reasoning agent message
            metadata: {
              isReasoningAgent: true,
              instruction: instruction,
              source: response.data.reasoningResult ? 'output.reasoning.result' : 'messageText',
            }
          });
        }

        // Apply the content to the document (from output.reasoning.result)
        if (onContentUpdate) {
          console.log('📝 Applying content to canvas from:', response.data.reasoningResult ? 'output.reasoning.result' : 'messageText');
          onContentUpdate(finalContent);
        }

        const changeId = applyChange({
          type: 'ai_edit',
          content: finalContent,
          oldContent: documentContent,
          instruction: instruction
        });

        console.log('✨ Change applied, waiting for user to accept or reject');

      } else {
        const errorMessage = response.error || 'No response from reasoning agent';
        console.error('❌ Reasoning agent error:', errorMessage);
        console.error('❌ Full response:', JSON.stringify(response, null, 2));
        throw new Error(errorMessage);
      }

    } catch (err) {
      console.error('❌ Request edit error:', err);
      setError(err.message || 'Failed to request edit');
      setIsThinking(false);
      setIsStreaming(false);
    }
  }, [reasoningThreadId, documentContent, onContentUpdate, onAddChatMessage, applyChange]);

  /**
   * Stop the current streaming operation
   */
  const stopStreaming = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }
    setIsStreaming(false);
    setIsThinking(false);
    isStreamingRef.current = false;
    streamBufferRef.current = '';
    setStreamedContent('');
  }, []);

  /**
   * Clear all pending changes
   */
  const clearPendingChanges = useCallback(() => {
    setPendingChanges([]);
  }, []);

  /**
   * Undo last confirmed change
   */
  const undoLastChange = useCallback(() => {
    if (editHistory.length === 0) return;
    
    const lastEdit = editHistory[editHistory.length - 1];
    setEditHistory(prev => prev.slice(0, -1));
    
    // This would need more sophisticated undo logic in production
    console.log('Undo:', lastEdit);
  }, [editHistory]);

  return {
    // State
    isStreaming,
    isThinking,
    pendingChanges,
    streamedContent,
    conversationId,
    threadId,
    reasoningThreadId, // Expose the reasoning thread ID
    error,
    editHistory,
    
    // NEW: DAG Progress
    dagProgress,
    currentDagNode,
    
    // Actions
    requestEdit,
    stopStreaming,
    applyChange,
    confirmChange,
    rejectChange,
    clearPendingChanges,
    undoLastChange,
    
    // Status
    isActive: isStreaming || isThinking,
    hasChanges: pendingChanges.length > 0
  };
};

export default useCollaborativeEdit;
